// ./src/utils/api/ThemeService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Display } from "./DisplayService";

// GraphQL queries
const THEME_LIST_QUERY = gql`
  query ThemeList($filter: ThemeFilter, $params: PaginationAndSorting) {
    themeList(filter: $filter, params: $params) {
      code
      data {
        id
        name
        description
        themeConfig
        rights
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const THEME_GET_QUERY = gql`
  query ThemeGet($id: ID) {
    themeGet(id: $id) {
      code
      data {
        id
        name
        description
        themeConfig
        rights
        status
        displays {
          themeId
          layoutId
          name
          description
          displayType
          roomId
          roomTypeId
          floor
          tag
          runningMessageId
          showPastHours
          showUpcomingHours
          emergencyContentBlockId
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const THEME_GET_MANY_QUERY = gql`
  query ThemeGetMany($ids: [ID]) {
    themeGetMany(ids: $ids) {
      code
      data {
        id
        name
        description
        themeConfig
        rights
        status
        displays {
          themeId
          layoutId
          name
          description
          displayType
          roomId
          roomTypeId
          floor
          tag
          runningMessageId
          showPastHours
          showUpcomingHours
          emergencyContentBlockId
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const THEME_CREATE_MUTATION = gql`
  mutation ThemeCreate($params: ThemeInput) {
    themeCreate(params: $params) {
      code
      data {
        id
        name
        description
        themeConfig
        rights
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const THEME_UPDATE_MUTATION = gql`
  mutation ThemeUpdate($where: ThemeWhereInput, $params: ThemeInput) {
    themeUpdate(where: $where, params: $params) {
      code
      data {
        id
        name
        description
        themeConfig
        rights
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const THEME_REMOVE_MUTATION = gql`
  mutation ThemeRemove($where: ThemeWhereInput) {
    themeRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface Theme {
  id?: string | undefined;
  name?: string | undefined;
  description?: string | null | undefined;
  themeConfig?: string | undefined;
  status?: number;
  displays: Display[];
}

export interface ThemeInput {
  name?: string | null | undefined;
  description?: string | null | undefined;
  themeConfig?: string | null | undefined;
  status?: number | null | undefined;
}

export interface ThemeFilter {
  id?: string[] | null | undefined;
  name?: string | null | undefined;
  status?: string | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface ThemeWhereInput {
  id?: string | undefined;
}

/**
 * theme service that handles all theme-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove
 */
export class ThemeService {
  /**
   * Get a list of themes with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: ThemeFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(THEME_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single theme by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(THEME_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple themes by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(THEME_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new theme
   */
  static async create(params: ThemeInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(THEME_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing theme
   */
  static async update(
    where: ThemeWhereInput,
    params: ThemeInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(THEME_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a theme
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(THEME_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }
}
